import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowSquareOut, FilePdf, Recycle, SealCheck } from '@phosphor-icons/react'
import { Badge, Banner, useToast } from '@/ui'
import { errorMessage } from '../../api/core'
import {
  latestPouchChecks,
  usePackLot,
  usePackLotDispatches,
  useReleaseChecklist,
  useReleaseDecisions,
  useUnitHolds,
  type PackLotDetail,
} from '../../api/lab'
import { ActionButton } from '../../components/Can'
import { ExceptionTable } from '../../components/ExceptionTable'
import { ConsolePage, DetailColumns, QueryState } from '../../components/Layout'
import { PhotoViewer } from '../../components/PhotoViewer'
import { Check, Code, Facts, Kg, Money, RefLink, Section, StatTile, Status, UserName, When } from '../../components/bits'
import { DelabelRunDialog } from '../../components/lab/DelabelRunDialog'
import { HeldUnitsList } from '../../components/lab/HeldUnits'
import { PackLotState } from '../../components/lab/PackLotState'
import { ReleaseChecklistView } from '../../components/lab/ReleaseChecklist'
import { ReleaseDecisionDialog } from '../../components/lab/ReleaseDecisionDialog'
import { openFile } from '../../lib/download'
import { DECISION_LABEL, DECISION_TONE, holdReasonText, unitLink } from '../../model/lab/labText'
import { humanize } from '../../labels'

/** One pack lot (F7.4-i): counts, label check, balance, lab, release checklist, decisions, trips. */
export function PackLotDetailPage() {
  const { id } = useParams<{ id: string }>()
  const q = usePackLot(id)
  const pl = q.data
  return (
    <ConsolePage
      back={{ to: '/c/pack-lots', label: 'Pack lots' }}
      eyebrow="Pack lot"
      title={pl ? <Code className="text-[inherit]">{pl.code}</Code> : 'Pack lot'}
      description={
        pl ? (
          <>
            {pl.skuName ?? 'Product'} · packed <When at={pl.packDate} dateOnly /> · best before <When at={pl.bestBefore} dateOnly />
          </>
        ) : undefined
      }
      actions={pl ? <PackLotState status={pl.status} hold={pl.hold} /> : undefined}
    >
      <QueryState query={q} label="this pack lot">
        {pl && <PackLotBody pl={pl} />}
      </QueryState>
    </ConsolePage>
  )
}

function PackLotBody({ pl }: { pl: PackLotDetail }) {
  const toast = useToast()
  const [deciding, setDeciding] = useState(false)
  const [delabel, setDelabel] = useState(false)
  const [printing, setPrinting] = useState<string | null>(null)
  const checklist = useReleaseChecklist(pl.id, pl.status !== 'PACKING')
  const decisions = useReleaseDecisions({ packLotId: pl.id, page: 1, limit: 50 })
  const dispatches = usePackLotDispatches(pl.id)
  const holds = useUnitHolds(pl.unitId)
  const held = holds.data?.held ?? []
  const checks = latestPouchChecks(pl.pouchChecks)
  const rec = pl.labelsReconciled
  const decidable = pl.status === 'CLOSED' || (pl.status === 'RELEASED' && pl.hold === 'HELD')

  const print = async (kind: 'coder-sheet' | 'carton-labels') => {
    setPrinting(kind)
    try {
      await openFile(`/pack-lots/${pl.id}/${kind}.pdf`, `${kind}-${pl.code}.pdf`)
    } catch (e) {
      toast.error(kind === 'coder-sheet' ? 'Coder sheet not ready' : 'Carton labels not ready', errorMessage(e))
    } finally {
      setPrinting(null)
    }
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <ActionButton
          perm="pack-lots:release"
          leadingIcon={<SealCheck />}
          onClick={() => setDeciding(true)}
          blockedReason={decidable ? null : pl.status === 'PACKING' ? 'The pack lot is still being packed.' : null}
          disabled={pl.status === 'DESTROYED'}
        >
          Release decision
        </ActionButton>
        <ActionButton perm="pack-lots:read" variant="secondary" leadingIcon={<FilePdf />} loading={printing === 'coder-sheet'} onClick={() => void print('coder-sheet')}>
          Coder sheet
        </ActionButton>
        <ActionButton perm="pack-lots:read" variant="secondary" leadingIcon={<FilePdf />} loading={printing === 'carton-labels'} onClick={() => void print('carton-labels')}>
          Carton labels
        </ActionButton>
        {(pl.hold !== 'NONE' || pl.status === 'WITHDRAWN') && pl.status !== 'DESTROYED' && (
          <ActionButton perm="units:dispose" hide variant="secondary" leadingIcon={<Recycle />} onClick={() => setDelabel(true)}>
            De-label run
          </ActionButton>
        )}
        <a
          href={`/p/${encodeURIComponent(pl.code)}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-10 items-center gap-1.5 rounded-chip px-3 text-[13px] text-ink underline decoration-rule underline-offset-2 hover:decoration-amber"
        >
          Product passport <ArrowSquareOut aria-hidden="true" />
        </a>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatTile label="Pouches" value={pl.pouchCount ?? '—'} hint={pl.netG ? `${pl.netG} g each` : undefined} />
        <StatTile label="Dispatched" value={pl.pouchesDispatched} />
        <StatTile label="Not dispatched" value={pl.pouchCount === null ? '—' : pl.pouchesLeft} />
        <StatTile label="Can be sold" value={pl.sellable ? 'Yes' : 'No'} tone={pl.sellable ? 'ok' : 'warn'} />
        <StatTile
          label="Lab result"
          value={pl.labStatus ? (pl.labStatus.overall === 'PENDING' ? 'Waiting' : humanize(pl.labStatus.overall)) : '—'}
          tone={pl.labStatus?.overall === 'PASS' ? 'ok' : pl.labStatus?.overall === 'FAIL' || pl.labStatus?.overall === 'NOT_ACCEPTED' ? 'danger' : 'warn'}
        />
      </div>

      {held.length > 0 && (
        <Section title="Held after a lab fail" eyebrow="Lab-fail hold" bodyClassName="p-0">
          <HeldUnitsList holds={holds.data!} />
        </Section>
      )}

      <DetailColumns
        main={
          <>
            <Section
              title="Release checklist"
              eyebrow="Checked by the system"
              actions={
                checklist.data && (
                  <Status status={checklist.data.canRelease ? 'PASS' : 'HOLD'} label={checklist.data.canRelease ? 'All green' : 'Not all green'} />
                )
              }
            >
              {pl.status === 'PACKING' ? (
                <p className="text-[14px] text-muted">The checklist is worked out once the pack run is closed.</p>
              ) : (
                <QueryState query={checklist} label="the checklist">
                  {checklist.data && <ReleaseChecklistView checklist={checklist.data} />}
                </QueryState>
              )}
            </Section>

            <Section title="Counts and labels">
              <Facts
                columns={4}
                items={[
                  { label: 'Codes printed', value: pl.labelsPrinted ?? '—', mono: true },
                  { label: 'Labels applied', value: pl.labelsApplied ?? '—', mono: true },
                  { label: 'Spoiled', value: pl.labelsSpoiled ?? '—', mono: true },
                  { label: 'Destroyed', value: pl.labelsDestroyed ?? '—', mono: true },
                  {
                    label: 'Codes add up',
                    value: (
                      <Check
                        ok={pl.labelsPrinted === null ? null : pl.reconciliation.ok}
                        label={
                          pl.labelsPrinted === null
                            ? 'Not closed yet'
                            : pl.reconciliation.ok
                              ? 'Yes'
                              : pl.reconciliation.gap !== null && pl.reconciliation.gap !== 0
                                ? `Off by ${pl.reconciliation.gap}`
                                : 'Pouches ≠ labels applied'
                        }
                      />
                    ),
                  },
                  { label: 'Pouches rejected', value: rec?.pouchesRejected ?? '—', mono: true },
                  {
                    label: 'Last pouch matches label',
                    value: <Check ok={rec?.lastPouchLabelOk ?? null} label={rec?.lastPouchLabelOk === null || !rec ? 'Not checked' : rec.lastPouchLabelOk ? 'Yes' : 'No'} />,
                  },
                  { label: 'Reconciled', value: <When at={rec?.at} /> },
                ]}
              />
              {rec?.counterPhotoId && (
                <div className="mt-3 flex items-center gap-3">
                  <PhotoViewer photo={rec.counterPhotoId} alt={`Coder counter, ${pl.code}`} size="sm" />
                  <span className="text-[13px] text-muted">Coder counter photo</span>
                </div>
              )}
            </Section>

            <Section title="First and last pouch">
              {checks.length === 0 ? (
                <p className="text-[14px] text-muted">No pouch scanned yet.</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {checks.map((c) => (
                    <li key={c.position} className="flex flex-wrap items-start gap-3">
                      <PhotoViewer photo={c.photoId} alt={`${c.position === 'FIRST' ? 'First' : 'Last'} pouch, ${pl.code}`} size="sm" />
                      <span className="flex min-w-0 flex-col gap-0.5 text-[14px]">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{c.position === 'FIRST' ? 'First pouch' : 'Last pouch'}</span>
                          <Check ok={c.match} label={c.match ? 'QR matches' : 'QR does not match'} />
                          {c.misses > 0 && <Badge tone="warn">{c.misses} earlier scan{c.misses === 1 ? '' : 's'} did not match</Badge>}
                        </span>
                        {c.scannedText && <Code className="break-all text-muted">{c.scannedText}</Code>}
                        <span className="text-[12.5px] text-muted">
                          <When at={c.at} />
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title="Weight balance" eyebrow={pl.runCode ?? undefined}>
              {pl.balance ? (
                <Facts
                  columns={4}
                  items={[
                    { label: 'Run', value: pl.balance.status ? <Status status={pl.balance.status} /> : '—' },
                    { label: 'In', value: <Kg grams={pl.balance.inputG} /> },
                    { label: 'Packed', value: <Kg grams={pl.balance.productG} /> },
                    { label: 'Unaccounted', value: <Kg grams={pl.balance.residualG} /> },
                    { label: 'Unaccounted %', value: pl.balance.residualPct ? `${pl.balance.residualPct} %` : '—', mono: true },
                    { label: 'Allowed loss', value: pl.balance.lossLimitPct ? `${pl.balance.lossLimitPct} %` : '—', mono: true },
                    { label: 'Yield', value: pl.balance.yieldPct ? `${pl.balance.yieldPct} %` : '—', mono: true },
                  ]}
                />
              ) : (
                <p className="text-[14px] text-muted">No balance yet.</p>
              )}
            </Section>

            <Section title="Decisions" bodyClassName="p-0">
              <QueryState query={decisions} label="the decisions">
                {(decisions.data?.rows.length ?? 0) === 0 ? (
                  <p className="px-4 py-3 text-[14px] text-muted">No decision yet.</p>
                ) : (
                  <ul className="divide-y divide-rule/70">
                    {decisions.data!.rows.map((d) => (
                      <li key={d.id} className="flex flex-wrap items-start justify-between gap-2 px-4 py-2.5 text-[14px]">
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-2">
                            <Status status={DECISION_TONE[d.decision] ?? d.decision} label={DECISION_LABEL[d.decision] ?? humanize(d.decision)} />
                            {d.auto && <Badge tone="neutral">Automatic</Badge>}
                            {d.qtyG !== null && <Kg grams={d.qtyG} className="text-muted" />}
                          </span>
                          {d.reason && <span className="mt-0.5 block text-ink">{holdReasonText(d.reason)}</span>}
                        </span>
                        <span className="shrink-0 text-right text-[12.5px] text-muted">
                          {d.decidedByName ?? <UserName id={d.decidedById} />}
                          <br />
                          <When at={d.decidedAt} />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </QueryState>
            </Section>

            <Section title="Trips to customers" bodyClassName="p-0">
              <QueryState query={dispatches} label="the trips">
                {(dispatches.data?.length ?? 0) === 0 ? (
                  <p className="px-4 py-3 text-[14px] text-muted">Not dispatched yet.</p>
                ) : (
                  <ul className="divide-y divide-rule/70">
                    {dispatches.data!.map((t) => (
                      <li key={`${t.shipmentId}:${t.pouches}`} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-[14px]">
                        <span className="flex flex-wrap items-center gap-2">
                          <Code>{t.shipmentCode}</Code>
                          <span>{t.toOrg ?? '—'}</span>
                          {t.challanNo && <span className="text-muted">· challan {t.challanNo}</span>}
                          {t.vehicleNo && <span className="font-mono text-[12.5px] text-muted">· {t.vehicleNo}</span>}
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="font-mono">{t.pouches} pouches</span>
                          <span className="text-[12.5px] text-muted">
                            <When at={t.dispatchedAt} />
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </QueryState>
            </Section>
          </>
        }
        side={
          <>
            <Section title="Pack lot">
              <Facts
                columns={2}
                items={[
                  { label: 'Code', value: pl.code, mono: true },
                  { label: 'Product', value: pl.skuCode, mono: true },
                  { label: 'Pack size', value: pl.netG ? `${pl.netG} g` : '—' },
                  { label: 'Per carton', value: pl.pouchesPerCarton ?? '—', mono: true },
                  { label: 'Packed', value: <When at={pl.packDate} dateOnly /> },
                  { label: 'Best before', value: <When at={pl.bestBefore} dateOnly /> },
                  { label: 'Label', value: pl.labelCode, mono: true },
                  { label: 'MRP', value: <Money paise={pl.mrpPaise} /> },
                  { label: 'Packed kg', value: <Kg grams={pl.packedG} /> },
                  {
                    label: 'Pack run',
                    value: pl.runId ? <RefLink to={`/c/pack-runs/${pl.runId}`}>{pl.runCode ?? 'Run'}</RefLink> : '—',
                  },
                  {
                    label: 'Batch',
                    value: pl.batchUnitId ? <RefLink to={unitLink('BATCH', pl.batchUnitId)!}>{pl.batchCode ?? 'Batch'}</RefLink> : '—',
                  },
                  { label: 'Run by', value: <UserName id={pl.operatorId} /> },
                  { label: 'Line cleaned by', value: <UserName id={pl.cleanoutById} /> },
                ]}
              />
              {pl.allergenStatement && <p className="mt-3 text-[13px] text-muted">{pl.allergenStatement}</p>}
              {pl.qrUrl && (
                <p className="mt-3 text-[13px]">
                  <span className="block font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">QR link</span>
                  <a href={pl.qrUrl} target="_blank" rel="noreferrer" className="break-all font-mono text-[12.5px] underline decoration-rule underline-offset-2">
                    {pl.qrUrl}
                  </a>
                </p>
              )}
            </Section>

            <Section title="Lab">
              {pl.labStatus ? (
                <div className="flex flex-col gap-3">
                  <Facts
                    columns={2}
                    items={[
                      { label: 'Result', value: <Status status={pl.labStatus.overall === 'PENDING' ? 'WAITING' : pl.labStatus.overall} label={pl.labStatus.overall === 'PENDING' ? 'Waiting for a checked report' : undefined} /> },
                      { label: 'Latest test', value: <When at={pl.labStatus.latestTestedAt} dateOnly /> },
                      { label: 'Tests needed', value: pl.labStatus.requiredPanels.map(humanize).join(', ') || '—' },
                      { label: 'Tests done', value: pl.labStatus.coveredPanels.map(humanize).join(', ') || '—' },
                    ]}
                  />
                  {pl.labStatus.reportIds.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {pl.labStatus.reportIds.map((rid, i) => (
                        <Link key={rid} to={`/c/lab-reports/${rid}`} className="text-[13px] underline decoration-rule underline-offset-2">
                          Lab report {pl.labStatus!.reportIds.length > 1 ? i + 1 : ''}
                        </Link>
                      ))}
                    </div>
                  )}
                  {pl.labStatus.labUnitId && (
                    <Link to={`/c/lab-reports?unitId=${pl.labStatus.labUnitId}`} className="text-[13px] underline decoration-rule underline-offset-2">
                      All reports for the batch
                    </Link>
                  )}
                </div>
              ) : (
                <p className="text-[14px] text-muted">No lab status.</p>
              )}
            </Section>

            <Section title="Problems" bodyClassName="p-0">
              <ExceptionTable rows={pl.exceptions} />
            </Section>
          </>
        }
      />

      {holds.isError && (
        <Banner tone="warn" title="Could not read the lab-fail holds">
          {errorMessage(holds.error)}
        </Banner>
      )}
      {deciding && <ReleaseDecisionDialog packLotId={pl.id} initialDecision={decidable ? 'RELEASE' : 'HOLD'} onClose={() => setDeciding(false)} />}
      {delabel && <DelabelRunDialog packLotId={pl.id} packLotCode={pl.code} onClose={() => setDelabel(false)} />}
    </>
  )
}
